import React, { useEffect, useState } from 'react';

import { useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';

import { MRT_Row } from 'material-react-table';

import BorderedSection from '../../shared/borderedSection';
import { PropertyAlertTriggerCardView } from '../property-alert-trigger-card-view';

import {
  EditableAlertTriggerSelectionCriteria,
  RowData,
} from '../types/propertyAlertTriggerRowData';
import Checkbox from '@mui/material/Checkbox';
import { convertToAirFlowUnits, convertToTempUnits } from '../../devices/util';
import {
  AirFlowUnit,
  Celsius,
  DefaultAirFlowUnit,
  Fahrenheit,
  LitersPerSecond,
  TemperatureUnit,
  DefaultTemperatureUnit,
} from '../../../system/models/temperatureUnits';
import { useAuthenticator } from '../../../auth/AuthenticationContext';
import { EnumAlertTriggerTriggerCriteriaSelectionCriteriaCriterionValueType } from '../../../../types/generated-types';
import { useIsSmallScreen } from '../../../system/SystemMediaQueriesProvider';

export interface PreferredUnits
  extends Record<
    EnumAlertTriggerTriggerCriteriaSelectionCriteriaCriterionValueType,
    any
  > {
  TEMPERATURE: TemperatureUnit;
  AIRFLOW: AirFlowUnit;
}

export interface PropertyAlertTriggerUpdateDialogProps {
  alertTriggerDialogOpen: boolean;
  handleSave: (updatedTrigger: RowData) => Promise<void>;
  handleCancel: () => void;
  selectedAlertTriggerRow: MRT_Row<RowData> | null;
}

export const PropertyAlertTriggerUpdateDialog = ({
  alertTriggerDialogOpen,
  handleSave,
  handleCancel,
  selectedAlertTriggerRow,
}: PropertyAlertTriggerUpdateDialogProps) => {
  const [currentEditableCriterion, setCurrentEditableCriterion] =
    useState<EditableAlertTriggerSelectionCriteria | null>(null);
  const [originalEditableCriterion, setOriginalEditableCriterion] =
    useState<EditableAlertTriggerSelectionCriteria | null>(null);

  const [currentEnabledStatus, setCurrentEnabledStatus] =
    useState<boolean>(false);
  const [originalEnabledStatus, setOriginalEnabledStatus] =
    useState<boolean>(false);

  const [hasChanges, setHasChanges] = useState(false);

  const theme = useTheme();
  const isSmall = useIsSmallScreen();

  useEffect(() => {
    if (selectedAlertTriggerRow) {
      setCurrentEnabledStatus(selectedAlertTriggerRow.original?.enabled);
      setOriginalEnabledStatus(selectedAlertTriggerRow.original?.enabled);

      const criterionToEdit =
        (selectedAlertTriggerRow?.original?.rawCriteria?.find(
          (criterion) => criterion?.editable,
        ) as EditableAlertTriggerSelectionCriteria) ?? null;
      if (criterionToEdit) {
        setOriginalEditableCriterion(criterionToEdit);
        setCurrentEditableCriterion({
          ...criterionToEdit,
        } as EditableAlertTriggerSelectionCriteria);
      } else {
        setOriginalEditableCriterion(null);
        setCurrentEditableCriterion(null);
      }
      setHasChanges(false);
    }
  }, [selectedAlertTriggerRow]);

  return alertTriggerDialogOpen && selectedAlertTriggerRow ? (
    <Dialog
      open={alertTriggerDialogOpen}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth={true}
      maxWidth="sm"
      sx={{
        '& h2': {
          fontSize: '18px !important',
        },
      }}
    >
      <DialogContent
        sx={{
          padding: '8px',
        }}
      >
        <DialogTitle
          id="alert-dialog-title"
          aria-labelledby="alert-dialog-title"
          component="h2"
          sx={{
            padding: 0,
            margin: '0px 0px 10px 0px',
            textAlign: 'center',
            fontSize: '1rem',
            fontWeight: 'bolder',
          }}
        >
          Edit Alert Trigger Value
        </DialogTitle>
        <DialogContent
          sx={
            isSmall
              ? {
                  margin: '0',
                  padding: '0',
                }
              : { marginTop: '10px', padding: '12px' }
          }
        >
          <BorderedSection
            className="current-alert-trigger-state"
            title="Current Alert Trigger State"
            ccStyle={{
              padding: 0,
              margin: '-6px 0px 0px 0px',
              borderStyle: 'none',
              width: '100%',
            }}
          >
            <Box
              sx={{
                '& .MuiCard-root': {
                  border: 'none',
                  boxShadow: 'none',
                  margin: '0',
                },
              }}
            >
              <PropertyAlertTriggerCardView row={selectedAlertTriggerRow} />
            </Box>
          </BorderedSection>
          <BorderedSection
            title="Edit Alert Trigger Value"
            style={{ marginTop: '30px' }}
          >
            <div>
              {currentEditableCriterion ? (
                selectedAlertTriggerRow.original?.levels === 1 ? (
                  <div style={{ display: 'flex', flexDirection: 'row' }}>
                    <TextField
                      fullWidth
                      autoFocus
                      required
                      id="value"
                      name="value"
                      variant="standard"
                      label="Comparison Value"
                      value={currentEditableCriterion.editableValues[0]}
                      onChange={(e) => {
                        setHasChanges(
                          e.target.value !==
                            `${originalEditableCriterion?.editableValues[0]}` ||
                            currentEnabledStatus !== originalEnabledStatus,
                        );
                        setCurrentEditableCriterion({
                          ...currentEditableCriterion,
                          editableValues: [e.target.value],
                        });
                      }}
                    />
                    <div style={{ marginTop: '24px' }}>
                      {currentEditableCriterion?.valueDisplayUnits}
                    </div>
                  </div>
                ) : (
                  <div>Unsupported edit mode: Multiple Levels Defined</div>
                )
              ) : (
                <Typography
                  style={{
                    fontSize: '14px',
                    fontWeight: 'bolder',
                  }}
                >
                  No editable criteria
                </Typography>
              )}
            </div>
          </BorderedSection>
          <BorderedSection
            title={'Enable/Disable Alert Trigger'}
            style={{ marginTop: '20px' }}
          >
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={currentEnabledStatus}
                    onChange={(e) => {
                      const newStatus = e.target.checked;
                      setHasChanges(
                        originalEditableCriterion?.editableValues[0] !==
                          currentEditableCriterion?.editableValues[0] ||
                          newStatus !== originalEnabledStatus,
                      );
                      setCurrentEnabledStatus(newStatus);
                    }}
                    inputProps={{ 'aria-label': 'primary checkbox' }}
                  />
                }
                label="Enable Alert Trigger"
              />
            </div>
          </BorderedSection>
          <div
            style={{
              margin: isSmall ? '8px 0px 0px 0px' : '10px 0px 6px 0px',
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              alignItems: 'center',
              padding: '4px',
            }}
          >
            <Button
              fullWidth
              style={
                isSmall
                  ? {
                      alignSelf: 'center',
                      display: 'flex',
                      flexDirection: 'row',
                      flex: '1 1 auto',
                      marginTop: '0px',
                      marginBottom: '0px',
                      width: '60%',
                      border: '1px solid darkgrey',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.8)',
                    }
                  : {
                      alignSelf: 'center',
                      display: 'flex',
                      flexDirection: 'row',
                      // marginLeft: '8px',
                      maxWidth: '60%',
                      // marginTop: '10px',
                      border: '1px solid darkgrey',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.8)',
                    }
              }
              disabled={!hasChanges}
              variant="contained"
              color="secondary"
              onClick={async () => {
                const updatedData = {
                  ...selectedAlertTriggerRow.original,
                  enabled: currentEnabledStatus,
                  rawCriteria: selectedAlertTriggerRow.original.rawCriteria.map(
                    (rawCriterion) => {
                      return rawCriterion?.editable
                        ? {
                            ...rawCriterion,
                            editableValues:
                              currentEditableCriterion?.editableValues ??
                              rawCriterion?.editableValues,
                          }
                        : rawCriterion;
                    },
                  ),
                };

                return await handleSave(updatedData);
              }}
            >
              Update
            </Button>
            <Button
              fullWidth
              style={
                isSmall
                  ? {
                      alignSelf: 'center',
                      display: 'flex',
                      flexDirection: 'row',
                      flex: '1 1 auto',
                      marginTop: '12px',
                      marginBottom: '0px',
                      width: '60%',
                      border: '1px solid darkgrey',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.8)',
                    }
                  : {
                      alignSelf: 'center',
                      display: 'flex',
                      flexDirection: 'row',
                      // marginLeft: '8px',
                      maxWidth: '60%',
                      marginTop: '20px',
                      border: '1px solid darkgrey',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.8)',
                    }
              }
              variant="contained"
              color="primary"
              onClick={handleCancel}
            >
              Close
            </Button>
            {/* TODO: Peter: in future, we will want to enable advanced editing of triggers allowing them to change
                 the name, description, targeted entity types and selection criteria */}
            {/*<Button*/}
            {/*  onClick={() => {*/}
            {/*    navigate(`./alert-triggers/${triggerId}/edit-alert-trigger`);*/}
            {/*  }}*/}
            {/*>*/}
            {/*  Advanced Editing*/}
            {/*</Button>*/}
          </div>
        </DialogContent>
      </DialogContent>
    </Dialog>
  ) : null;
};
